import { PaymentFormProps } from "../../../containers/agreement/Invoice/components/InvoicePaymentForm";
import { Dict } from "../../models";
import {
  ApiResponse,
  TokenData,
  ProposalReason,
  ProposedPropertyDetails,
  DraftProposalStatus,
  UpdateSendProposalStatusDto,
  Proposal,
  PaginatedApiResponse,
  ILeadActivity,
  PaidBy,
  ListSort,
} from "../models";
import { CreateTenancyCondition } from "../models/tenancyCondition";
import * as http from "./http";
import ApiService from "./service";

export class ProposalDocumentService extends ApiService {
  public async loadAllProposalDocumentSettings(displayTab: string[]) {
    const url = `${this.apiDomain}/api/proposal-document-settings/list`;

    const resposne = await http.post<ApiResponse<ProposalReason[]>>(
      url,
      { displayTab },
      this.store
    );
    return resposne?.data?.data;
  }

  public async createProposalDocumentStage(data: any) {
    const url = `${this.apiDomain}/api/proposal-document`;

    const response = await http.post<ApiResponse<any>>(url, data, this.store);
    return response.data?.data;
  }

  public async listProposalDocumentStages(
    requirementId?: string
  ): Promise<ProposedPropertyDetails> {
    const url = `${this.apiDomain}/api/proposal-document/docs/${requirementId}`;

    const resposne = await http.get<ApiResponse<ProposedPropertyDetails>>(
      url,
      this.store
    );
    return resposne.data?.data;
  }

  public async findGenericProposalDocumentStageById(
    documentId?: string
  ): Promise<ProposedPropertyDetails> {
    const url = `${this.apiDomain}/api/proposal-document/get/${documentId}`;

    const resposne = await http.get<ApiResponse<ProposedPropertyDetails>>(
      url,
      this.store
    );
    return resposne.data?.data;
  }

  public async getInvoicePaymentHistory(data: {
    invoiceId?: string,
    invoiceCopyType: PaidBy,
    fromDate: Date | undefined,
    toDate: Date | undefined,
    isRefunded?: boolean,
  }): Promise<ProposedPropertyDetails> {
    const url = `${this.apiDomain}/api/invoice/payment-history`;

    const response = await http.post<ApiResponse<any>>(url, data, this.store);
    return response.data?.data;
  }

  public async requestApproval(data: any, id: string) {
    const url = `${this.apiDomain}/api/proposal-document/approval/request/${id}`;

    const response = await http.put<ApiResponse<any>>(url, data, this.store);
    return response.data?.data;
  }

  public async internalApproval(data: any, id: string) {
    const url = `${this.apiDomain}/api/proposal-document/approval/internal/${id}`;

    const response = await http.put<ApiResponse<any>>(url, data, this.store);
    return response.data?.data;
  }

  public async rejectApproval(data: any, id: string) {
    const url = `${this.apiDomain}/api/proposal-document/reject/${id}`;

    const response = await http.put<ApiResponse<any>>(url, data, this.store);
    return response.data?.data;
  }

  public async approveOnSigned(data: any, id: string) {
    const url = `${this.apiDomain}/api/proposal-document/approval/signed/${id}`;

    const response = await http.put<ApiResponse<any>>(url, data, this.store);
    return response.data?.data;
  }

  public async getActivities(
    id: string,
    status: "completed" | "planned",
    filter?: {
      page?: number;
      type: string;
    }
  ): Promise<PaginatedApiResponse<ILeadActivity>> {
    let url = `${this.apiDomain}/api/proposal-document/${id}/activities?status=${status}`;
    if (filter?.type) {
      url += `&type=${filter?.type}`;
    }
    if (filter?.page != null) {
      url += `&page=${filter.page}`;
    }
    const response = await http.get<PaginatedApiResponse<ILeadActivity>>(
      url,
      this.store
    );

    return response?.data;
  }

  public async markAsPaid(id: string, data: { amount: number }) {
    const url = `${this.apiDomain}/api/proposal-document/mark-as-paid/${id}`;
    const response = await http.put<ApiResponse<any>>(url, data, this.store);
    return response.data?.data;
  }

  public async markAsPaidTransaction(data: PaymentFormProps, id:string) {
    const url = `${this.apiDomain}/api/proposal-document/invoice-payment/${id}`;
    try {
      const response = await http.put<ApiResponse<any>>(url, data, this.store);
      return response;
    } catch (error) {
      return error?.response;
      
    }
  }

  public async createCommission(data: any) {
    const url = `${this.apiDomain}/api/commission`;
    const response = await http.post<ApiResponse<any>>(url, data, this.store);
    return response.data?.data;
  }
  public async getCommissions(requirementId: string) {
    const url = `${this.apiDomain}/api/commission/get/${requirementId}`;
    const response = await http.post<ApiResponse<any>>(url, {}, this.store);
    return response.data?.data;
  }
  public async editCommissions(data: any, id: string) {
    const url = `${this.apiDomain}/api/commission/update/${id}`;
    const response = await http.post<ApiResponse<any>>(url, data, this.store);
    return response.data?.data;
  }

  public async createTenancyCondition(data: CreateTenancyCondition) {
    const url = `${this.apiDomain}/api/tenancy-condition`;
    const response = await http.post<ApiResponse<any>>(url, data, this.store);
    return response.data?.data;
  }
  public async editTenancyCondition(data: any, id: string) {
    const url = `${this.apiDomain}/api/tenancy-condition/update/${id}`;
    const response = await http.post<ApiResponse<any>>(url, data, this.store);
    return response.data?.data;
  }
  public async getTenancyConditions(requirementId: string) {
    const url = `${this.apiDomain}/api/tenancy-condition/list`;
    const response = await http.post<ApiResponse<any>>(
      url,
      { requirementId },
      this.store
    );
    return response.data?.data;
  }

  public async getSignedDocumentsList(
    documentId?: string,
    documentCopyType?: string
  ): Promise<ProposedPropertyDetails> {
    const url = `${this.apiDomain}/api/proposal-document/signed-docs`;

    const resposne = await http.post<ApiResponse<ProposedPropertyDetails>>(
      url,
      {
        documentId,
        documentCopyType,
      },
      this.store
    );
    return resposne.data?.data;
  }

  public async getProposalDocumentList(
    data: {
      page?: number;
      search?: string;
      perPage?: number;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<any[]>> {
    const url = `${this.apiDomain}/api/proposal-document/list`;

    const response = await http.post<PaginatedApiResponse<any[]>>(
      url,
      data,
      this.store
    );

    return response?.data;
  }

  public async getFeeLetterList(
    data: {
      page?: number;
      search?: string;
      perPage?: number;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<any[]>> {
    const url = `${this.apiDomain}/api/proposal-document/list`;

    const response = await http.post<PaginatedApiResponse<any[]>>(
      url,
      data,
      this.store
    );

    return response?.data;
  }
}